import type { Dispatch, SetStateAction } from "react";
import AdminSection from "../../../shared/components/AdminSection";
import Button from "../../../shared/components/Button";
import ImageUploadField from "../../../shared/components/ImageUploadField";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import { formatEventDateRangeLabel } from "../../../shared/utils/dateTime";
import type { ChallengeHistoryItem } from "../../challenges/types/challengeAdmin";
import { challengeTypeLabel } from "../../challenges/utils/challengeLabels";
import type { SiteContent } from "../../home/shared/types/siteContent";
import type { AdminEventChallengeLink } from "../services/eventRegistrationAdminService";

type AdminContentEventsPanelProps = {
  addListItem: (section: "events") => void;
  contentDraft: SiteContent;
  eventChallengeLinkDrafts: Record<string, string>;
  eventChallengeLinks: Record<string, AdminEventChallengeLink>;
  eventChallengeLinksLoading: boolean;
  linkableEventChallengesSorted: ChallengeHistoryItem[];
  onDeleteEventLink: (eventId: string) => Promise<void>;
  onUpsertEventLink: (eventId: string) => Promise<void>;
  removeListImage: (section: "events", index: number) => Promise<void>;
  removeListItem: (section: "events", index: number) => void;
  setEventChallengeLinkDrafts: Dispatch<SetStateAction<Record<string, string>>>;
  updateListItem: (
    section: "events",
    index: number,
    field: string,
    value: string,
  ) => void;
};

export default function AdminContentEventsPanel({
  addListItem,
  contentDraft,
  eventChallengeLinkDrafts,
  eventChallengeLinks,
  eventChallengeLinksLoading,
  linkableEventChallengesSorted,
  onDeleteEventLink,
  onUpsertEventLink,
  removeListImage,
  removeListItem,
  setEventChallengeLinkDrafts,
  updateListItem,
}: AdminContentEventsPanelProps) {
  const t = siteTexts.admin;

  return (
    <AdminSection
      title={t.sections.events}
      action={
        <Button type="button" variant="pill" onClick={() => addListItem("events")}>
          {t.actions.addEvent}
        </Button>
      }
    >
      <div className="admin-list">
        {contentDraft.events.map((item, index) => {
          const eventCode = String((item as any)?.id || "").trim();
          const eventIdValid =
            Boolean(eventCode) &&
            eventCode.length <= 80 &&
            /^[A-Za-z0-9_-]+$/.test(eventCode);
          const linked = eventCode ? eventChallengeLinks[eventCode] : null;
          const rawTournamentDraft = eventCode
            ? String(eventChallengeLinkDrafts[eventCode] || "").trim()
            : "";
          const draftTournamentId = Number(rawTournamentDraft);
          const tournamentIdValid =
            Number.isInteger(draftTournamentId) && draftTournamentId > 0;
          return (
            <div key={`event-${index}`} className="admin-card">
              <label className="field">
                <span>{t.eventLink.eventCode}</span>
                <input
                  type="text"
                  value={String((item as any)?.id || "")}
                  onChange={(event) =>
                    updateListItem("events", index, "id", event.target.value)
                  }
                />
              </label>
              <label className="field">
                <span>{t.labels.title}</span>
                <input
                  type="text"
                  value={item.title}
                  onChange={(event) =>
                    updateListItem("events", index, "title", event.target.value)
                  }
                />
              </label>
              <label className="field">
                <span>{t.labels.date}</span>
                <input
                  type="text"
                  placeholder="dd/mm/aaaa - HH:MM (24h)"
                  value={item.date}
                  onChange={(event) =>
                    updateListItem("events", index, "date", event.target.value)
                  }
                />
              </label>
              <label className="field">
                <span>{t.labels.address}</span>
                <input
                  type="text"
                  value={item.address}
                  onChange={(event) =>
                    updateListItem(
                      "events",
                      index,
                      "address",
                      event.target.value,
                    )
                  }
                />
              </label>
              <label className="field">
                <span>{t.labels.description}</span>
                <textarea
                  value={item.description}
                  onChange={(event) =>
                    updateListItem(
                      "events",
                      index,
                      "description",
                      event.target.value,
                    )
                  }
                  rows={3}
                />
              </label>
              <label className="field">
                <span>{t.labels.fullDetails}</span>
                <textarea
                  value={item.details}
                  onChange={(event) =>
                    updateListItem(
                      "events",
                      index,
                      "details",
                      event.target.value,
                    )
                  }
                  rows={5}
                />
              </label>
              <label className="field">
                <span>{t.eventLink.registration}</span>
                <p className="status-message">{t.eventLink.registrationHint}</p>
              </label>
              <div className="admin-event-link">
                <span>{t.eventLink.challengeLink}</span>
                <div className="admin-event-link-row">
                  <select
                    value={eventCode ? eventChallengeLinkDrafts[eventCode] || "" : ""}
                    onChange={(event) => {
                      if (!eventCode) return;
                      setEventChallengeLinkDrafts((prev) => ({
                        ...prev,
                        [eventCode]: event.target.value,
                      }));
                    }}
                    disabled={
                      eventChallengeLinksLoading || !eventCode || !eventIdValid
                    }
                  >
                    <option value="">{t.eventLink.selectChallenge}</option>
                    {linkableEventChallengesSorted.map((challenge) => {
                      const when = formatEventDateRangeLabel(
                        challenge.scheduledAt || challenge.createdAt,
                        challenge.scheduledEndAt,
                      );
                      return (
                        <option
                          key={`linkable-challenge-${challenge.id}`}
                          value={String(challenge.id)}
                        >
                          #{challenge.id} · {challenge.challengeName} ·{" "}
                          {challengeTypeLabel(challenge.challengeType)} ·{" "}
                          {challenge.status} · {when}
                        </option>
                      );
                    })}
                  </select>
                  <input
                    type="number"
                    inputMode="numeric"
                    placeholder={t.eventLink.challengeIdPlaceholder}
                    value={eventCode ? eventChallengeLinkDrafts[eventCode] || "" : ""}
                    onChange={(event) => {
                      if (!eventCode) return;
                      setEventChallengeLinkDrafts((prev) => ({
                        ...prev,
                        [eventCode]: event.target.value,
                      }));
                    }}
                    disabled={!eventCode || !eventIdValid}
                  />
                  <Button
                    type="button"
                    variant="pill"
                    onClick={() => onUpsertEventLink(eventCode)}
                    disabled={
                      eventChallengeLinksLoading ||
                      !eventCode ||
                      !eventIdValid ||
                      !tournamentIdValid
                    }
                  >
                    {t.eventLink.link}
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() => onDeleteEventLink(eventCode)}
                    disabled={eventChallengeLinksLoading || !linked}
                  >
                    {t.eventLink.removeLink}
                  </Button>
                </div>
                <p className="status-message">
                  {!eventCode
                    ? t.eventLink.defineEventId
                    : !eventIdValid
                      ? t.eventLink.invalidEventId
                      : linked
                        ? `${t.eventLink.linkedToChallengePrefix} #${linked.tournamentId} (${linked.challengeType}).`
                        : t.eventLink.noLink}
                </p>
                {eventCode && linkableEventChallengesSorted.length === 0 && (
                  <p className="status-message">{t.eventLink.noChallengeFound}</p>
                )}
              </div>
              <ImageUploadField
                label={t.labels.image}
                value={item.imageUrl}
                previewSquare
                onChange={(nextValue) =>
                  updateListItem("events", index, "imageUrl", nextValue)
                }
                onRemove={() => removeListImage("events", index)}
              />
              <Button
                type="button"
                variant="danger"
                onClick={() => removeListItem("events", index)}
              >
                {siteTexts.common.remove}
              </Button>
            </div>
          );
        })}
      </div>
    </AdminSection>
  );
}
